import { TeamsApi, type TeamWithCountsDto } from '@up2stay/up2promo-api-client';
import {
  Checkbox,
  Skeleton,
  cn,
  ghostAccentIconTileClass,
} from '@up2stay/up2promo-ui';
import { Check, Users, UsersRound } from 'lucide-react';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  TEMPLATE_DETAIL_MUTED_CLASS,
  TEMPLATE_DETAIL_SECTION_TITLE_CLASS,
  TemplateDetailSectionCard,
} from '@/core/components/profile-templates/template-detail-section-card';
import { useScrollShadow } from '@/core/hooks/use-scroll-shadow';

const TEAM_LIST_SCROLL_THRESHOLD = 2;

interface TemplateTeamsAssignmentCardProps {
  workspaceId: string;
  teamIds: string[];
  canManage: boolean;
  dict: {
    title: string;
    description: string;
    emptyTeams: string;
    selectionCount?: string;
    noSelection?: string;
    members?: string;
  };
  onTeamIdsChange: (next: string[]) => void;
  variant?: 'card' | 'embedded';
  /** Fixed card height to match Stammdaten card in two-column layout. */
  matchCardHeight?: number;
}

function teamAccentStyle(color: string | undefined): CSSProperties {
  if (color?.trim()) {
    return { backgroundColor: color };
  }
  return {};
}

function ScrollableTeamList({
  children,
  measureKey,
  fadeFromClass,
  fillAvailable,
}: {
  children: ReactNode;
  measureKey: string | number;
  fadeFromClass: string;
  fillAvailable?: boolean;
}) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null,
  );

  const { showTopShadow, showBottomShadow } = useScrollShadow({
    scrollElement,
    contentElement,
    threshold: TEAM_LIST_SCROLL_THRESHOLD,
    measureRevision: measureKey,
  });

  return (
    <div
      className={cn('relative min-h-0', fillAvailable && 'flex min-h-0 flex-1')}
    >
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b to-transparent transition-opacity duration-200',
          fadeFromClass,
          showTopShadow ? 'opacity-100' : 'opacity-0',
        )}
        aria-hidden
      />
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t to-transparent transition-opacity duration-200',
          fadeFromClass,
          showBottomShadow ? 'opacity-100' : 'opacity-0',
        )}
        aria-hidden
      />
      <div
        ref={setScrollElement}
        className={cn(
          'overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
          fillAvailable ? 'min-h-0 flex-1' : 'max-h-56',
        )}
      >
        <div ref={setContentElement} className="flex flex-col gap-2">
          {children}
        </div>
      </div>
    </div>
  );
}

export function TemplateTeamsAssignmentCard({
  workspaceId,
  teamIds,
  canManage,
  dict,
  onTeamIdsChange,
  variant = 'card',
  matchCardHeight,
}: TemplateTeamsAssignmentCardProps) {
  const heightMatched = variant === 'card' && matchCardHeight != null;
  const [teams, setTeams] = useState<TeamWithCountsDto[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTeams = useCallback(async () => {
    setLoading(true);
    try {
      const res = await TeamsApi.findAll({
        path: { workspaceId },
        query: { page: 1, limit: 100 },
      });
      setTeams((res.data?.data ?? []) as TeamWithCountsDto[]);
    } catch (err) {
      console.error(err);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void (async () => {
      await loadTeams();
    })();
  }, [loadTeams]);

  const toggleTeam = (teamId: string, checked: boolean) => {
    if (!canManage) return;
    const next = checked
      ? [...new Set([...teamIds, teamId])]
      : teamIds.filter((id) => id !== teamId);
    onTeamIdsChange(next);
  };

  const selectedCount = teamIds.length;
  const membersLabel = dict.members ?? 'Mitglieder';
  const listMeasureKey = `${teams.length}-${[...teamIds].sort().join(',')}`;

  const teamList = loading ? (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-14 w-full rounded-[var(--r-control)]" />
      <Skeleton className="h-14 w-full rounded-[var(--r-control)]" />
      <Skeleton className="h-14 w-full rounded-[var(--r-control)]" />
    </div>
  ) : teams.length === 0 ? (
    <div className="flex flex-col items-center justify-center gap-2 rounded-[var(--r-control)] border border-dashed border-border/80 bg-background/50 px-4 py-8 text-center">
      <UsersRound className="h-8 w-8 text-muted-foreground/60" />
      <p className={cn(TEMPLATE_DETAIL_MUTED_CLASS, 'max-w-xs')}>
        {dict.emptyTeams}
      </p>
    </div>
  ) : (
    <ScrollableTeamList
      measureKey={listMeasureKey}
      fadeFromClass={variant === 'embedded' ? 'from-muted/20' : 'from-card'}
      fillAvailable={heightMatched}
    >
      <ul className="flex flex-col gap-2">
        {teams.map((team) => {
          const checked = teamIds.includes(team.id);
          const rowId = `template-team-${team.id}`;
          return (
            <li key={team.id}>
              <button
                type="button"
                disabled={!canManage}
                aria-pressed={checked}
                className={cn(
                  'flex w-full items-center gap-3 rounded-[var(--r-control)] border px-3 py-2.5 text-left transition-[color,background-color,border-color] duration-150',
                  checked
                    ? 'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)]'
                    : 'border-border/60 bg-background hover:bg-muted/30',
                  !canManage && 'cursor-default opacity-90',
                )}
                onClick={() => toggleTeam(team.id, !checked)}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                    !team.color?.trim() && 'bg-muted text-muted-foreground',
                  )}
                  style={teamAccentStyle(team.color)}
                  aria-hidden
                >
                  {!team.color?.trim() ? <Users className="h-4 w-4" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-semibold leading-tight">
                    {team.name}
                  </span>
                  {team.description?.trim() ? (
                    <span className="block truncate text-[13.5px] leading-relaxed text-muted-foreground">
                      {team.description}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-[13.5px] tabular-nums text-muted-foreground">
                  {team.memberCount} {membersLabel}
                </span>
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                    checked
                      ? 'border-transparent bg-[image:var(--brand-grad)] text-white'
                      : 'border-muted-foreground/30 bg-background',
                  )}
                  aria-hidden
                >
                  {checked ? <Check className="h-3 w-3" /> : null}
                </span>
                <Checkbox
                  id={rowId}
                  checked={checked}
                  disabled={!canManage}
                  className="sr-only"
                  tabIndex={-1}
                  onCheckedChange={(value) =>
                    toggleTeam(team.id, value === true)
                  }
                />
              </button>
            </li>
          );
        })}
      </ul>
    </ScrollableTeamList>
  );

  const noSelectionHint =
    canManage &&
    !loading &&
    teams.length > 0 &&
    selectedCount === 0 &&
    dict.noSelection ? (
      <p className="text-[13.5px] font-medium text-amber-800 dark:text-amber-300">
        {dict.noSelection}
      </p>
    ) : null;

  if (variant === 'embedded') {
    return (
      <div className="flex min-w-0 flex-col gap-3">
        {teamList}
        {noSelectionHint}
      </div>
    );
  }

  return (
    <TemplateDetailSectionCard
      className={cn(
        'min-w-0 w-full',
        heightMatched && 'flex h-full min-h-0 flex-col overflow-hidden',
      )}
      style={heightMatched ? { height: matchCardHeight } : undefined}
      contentClassName={cn(
        'min-h-0 gap-3 overflow-hidden',
        heightMatched && 'flex flex-1 flex-col',
      )}
    >
      <div className="flex shrink-0 flex-col gap-2">
        <div className="flex items-center gap-2.5">
          <div
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-[var(--r-control)]',
              ghostAccentIconTileClass,
            )}
            aria-hidden
          >
            <Users className="size-4" />
          </div>
          <h2 className={TEMPLATE_DETAIL_SECTION_TITLE_CLASS}>{dict.title}</h2>
        </div>
        <p className={TEMPLATE_DETAIL_MUTED_CLASS}>{dict.description}</p>
      </div>
      <div
        className={cn(
          'min-h-0',
          heightMatched && 'flex flex-1 flex-col overflow-hidden',
        )}
      >
        {teamList}
      </div>
      {noSelectionHint}
    </TemplateDetailSectionCard>
  );
}
